import { Injectable } from '@angular/core';
import Swal from 'sweetalert2';
import { environment } from '../../environments/environment';

/**
 * Aviso de migración a la app nueva (React), servida en el MISMO dominio bajo /beta/.
 *
 * Mismo origen ⇒ localStorage compartido: no hace falta handoff de sesión, basta redirigir.
 *
 * Flujo:
 *  - Si el usuario ya eligió la nueva versión ("Probar la nueva versión") → al entrar a la
 *    clásica se le redirige AUTOMÁTICO a /beta, siempre (con opción de volver desde React).
 *  - Si aún no elige → se muestra el aviso, pero como MUCHO una vez al día (guard por fecha).
 *  - Loop-break: al volver desde la app nueva con ?clasica=1 se cancela el auto-redirect
 *    (modo 'clasica') para que se pueda usar la clásica sin rebotar a /beta.
 */
const FECHA_KEY = 'oadmin_aviso_beta_fecha';
const MODO_KEY = 'oadmin_modo_beta'; // 'nueva' = auto-redirige siempre | 'clasica' = quedarse

@Injectable({ providedIn: 'root' })
export class MigracionBetaService {
	/** Llamar cuando hay sesión activa (en cada ingreso a la app). Idempotente por carga. */
	revisar(): void {
		const betaURL = (environment as any).betaURL as string;

		// 1) Regreso explícito desde la app nueva ("volver a la clásica"): cancelar auto-redirect.
		if (this.tieneFlagClasica()) {
			this.guardarModo('clasica');
			this.marcarHoy();
			this.limpiarFlagClasica();
			return;
		}

		if (!betaURL) return; // aviso desactivado en este entorno
		if (!window.localStorage.getItem('usu') || !window.localStorage.getItem('authToken')) return;

		// 2) Ya eligió la nueva versión: mandarlo directo, siempre.
		if (this.leerModo() === 'nueva') {
			this.irABeta(betaURL);
			return;
		}

		// 3) Aún no elige: aviso 1×/día.
		if (this.yaAvisadoHoy()) return;
		this.marcarHoy();
		this.mostrarAviso(betaURL);
	}

	private mostrarAviso(betaURL: string): void {
		Swal.fire(<any>{
			title: '¡Estrenamos nueva versión de oAdmin! 🚀',
			html:
				'<p style="margin:.5rem 0 0">oAdmin se está actualizando. Prueba la <b>nueva versión</b>: ' +
				'<b>más rápida</b> y con <b>más opciones</b>. Tu cuenta y tus datos son los mismos, ' +
				'entras directo <b>sin volver a iniciar sesión</b>.</p>' +
				'<p style="margin:.75rem 0 0;font-size:.9em;color:#777">Puedes volver a la versión clásica cuando quieras.</p>',
			imageUrl: 'assets/icons/icon-192x192.png',
			imageWidth: 88,
			imageHeight: 88,
			imageAlt: 'oAdmin',
			showCancelButton: true,
			confirmButtonText: 'Probar la nueva versión',
			cancelButtonText: 'Ahora no',
			confirmButtonColor: '#d20a31',
			cancelButtonColor: '#6c757d',
			reverseButtons: true,
			allowOutsideClick: false,
		}).then((res) => {
			if (res.isConfirmed) {
				this.guardarModo('nueva'); // recuerda la elección → próximos ingresos van directo
				this.irABeta(betaURL);
			}
			// "Ahora no": no persiste nada, vuelve a preguntar mañana (guard diario).
		});
	}

	private leerModo(): string | null {
		return window.localStorage.getItem(MODO_KEY);
	}

	private guardarModo(modo: 'nueva' | 'clasica'): void {
		window.localStorage.setItem(MODO_KEY, modo);
	}

	private irABeta(betaURL: string): void {
		window.location.href = betaURL.endsWith('/') ? betaURL : betaURL + '/';
	}

	private hoy(): string {
		return new Date().toISOString().slice(0, 10); // YYYY-MM-DD
	}

	private yaAvisadoHoy(): boolean {
		return window.localStorage.getItem(FECHA_KEY) === this.hoy();
	}

	private marcarHoy(): void {
		window.localStorage.setItem(FECHA_KEY, this.hoy());
	}

	private tieneFlagClasica(): boolean {
		return window.location.href.indexOf('clasica=1') > -1;
	}

	private limpiarFlagClasica(): void {
		try {
			const url = new URL(window.location.href);
			url.searchParams.delete('clasica');
			window.history.replaceState(null, '', url.toString());
		} catch {
			/* noop */
		}
	}
}
